import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";
import { getDiscountedPrice } from './SignUpPage'; // Updated import

const sendNotificationEmails = async (userDetails, planDetails) => {
  try {
    console.log('Sending notification emails:', { userDetails, planDetails });
    
    const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    console.log('Using API URL:', `${apiBaseUrl}/api/send-email`);
    
    const response = await fetch(`${apiBaseUrl}/api/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
      },
      body: JSON.stringify({
        adminEmail: {
          to: 'support@fxbullsheet.com',
          userInfo: {
            fullName: userDetails.fullName,
            email: userDetails.email,
            plan: userDetails.plan,
            isTrial: userDetails.isTrial || false,
            paymentMethod: userDetails.paymentMethod
          }
        },
        userEmail: {
          to: userDetails.email,
          fullName: userDetails.fullName,
          planInfo: {
            plan: userDetails.plan,
            cost: planDetails.cost,
            nextBillingDate: planDetails.nextBillingDate,
            discountApplied: planDetails.discountApplied,
            paymentMethod: userDetails.paymentMethod
          }
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    console.log('Notification emails sent successfully');
  } catch (error) {
    console.error('Error sending notification emails:', error);
  }
};

function SubscriptionSuccess() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const completeSubscription = async () => {
      try {
        const pendingSignup = JSON.parse(sessionStorage.getItem('pendingSignup'));
        if (!pendingSignup) {
          throw new Error('No pending signup found');
        }
    
        const {
          email,
          password,
          fullName,
          plan,
          subscriptionID,
          discountCode,
          discountInfo,
          isTrial,
          paymentMethod,
          teamId
        } = pendingSignup;
    
        if (!email || !email.includes('@')) {
          throw new Error('Invalid email address.');
        }
        if (!password || password.length < 8) {
          throw new Error('Invalid password.');
        }
    
        const auth = getAuth();
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
    
        console.log('User created with UID:', user.uid);
    
        const userDocData = {
          fullName,
          email,
          plan,
          subscriptionID,
          discountCode: discountCode || null,
          paymentMethod,
          createdAt: new Date().toISOString()
        };
    
        if (teamId) {
          userDocData.profileInfo = {
            role: 'member',
            teamId,
            teamRole: 'member',
            teamPermissions: { [teamId]: false }
          };
        }
    
        await setDoc(doc(db, 'Users', user.uid), userDocData);
    
        console.log('User information saved to Firestore.');
    
        const isMonthly = plan === 'monthly_plan' || isTrial;
        const standardCost = isMonthly ? 19.99 : 190;
        const discountedCost = discountInfo?.value 
          ? getDiscountedPrice(standardCost, plan, discountInfo, null) 
          : standardCost;
    
        const planDetails = {
          cost: discountedCost,
          originalCost: standardCost,
          nextBillingDate: new Date(Date.now() + 
            (isTrial ? 7 : isMonthly ? 30 : 365) * 24 * 60 * 60 * 1000).toISOString(),
          discountApplied: discountInfo ? {
            code: discountCode,
            value: discountInfo.value,
            type: discountInfo.type
          } : null
        };
    
        // Fetch stripeCustomerId if payment method is Stripe
        let stripeCustomerId = null;
        if (paymentMethod === 'stripe') {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Origin': window.location.origin,
            },
            body: JSON.stringify({
              sessionId: subscriptionID
            })
          });
    
          if (!response.ok) {
            throw new Error(`Failed to fetch checkout session: ${response.statusText}`);
          }
    
          const sessionData = await response.json();
          stripeCustomerId = sessionData.customer; // Retrieve customer ID (cus_...)
          if (!stripeCustomerId) {
            throw new Error('No customer ID found in Stripe checkout session');
          }
          console.log('Retrieved stripeCustomerId:', stripeCustomerId);
        }
    
        await setDoc(doc(db, 'subscriptions', user.uid), {
          userId: user.uid,
          type: isMonthly ? 'monthly' : 'annual',
          status: 'active',
          [paymentMethod === 'stripe' ? 'stripeSubscriptionId' : 'paypalSubscriptionId']: subscriptionID,
          ...(paymentMethod === 'stripe' && { stripeCustomerId }), // Save stripeCustomerId
          createdAt: new Date().toISOString(),
          plan,
          isTrial: !!isTrial,
          planDetails
        });
    
        await sendNotificationEmails(
          {
            fullName,
            email,
            plan,
            subscriptionID,
            discountCode,
            isTrial,
            paymentMethod
          },
          planDetails
        );
    
        console.log('Subscription details saved and notifications sent.');
        
        localStorage.setItem('userID', user.uid);
        sessionStorage.removeItem('pendingSignup');
        
        setUserInfo({
          fullName,
          plan,
          isTrial
        });
    
      } catch (err) {
        console.error('Subscription completion failed:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    completeSubscription();
  }, [navigate]);

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox p={3}>
        <MDTypography color="error" variant="h6">
          {error}
        </MDTypography>
        <MDTypography variant="body1">
          Please try again or contact support if the issue persists.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3} textAlign="center">
      <MDTypography variant="h4" gutterBottom>
        Thank you for signing up, {userInfo?.fullName}!
      </MDTypography>
      <MDTypography variant="h6" gutterBottom>
        {userInfo?.isTrial 
          ? "You've successfully started your 7-day free trial."
          : `You've successfully subscribed to the ${
              userInfo?.plan === 'monthly_plan' ? 'Monthly' : 'Annual'
            } Plan.`}
      </MDTypography>
      <MDTypography variant="body1" gutterBottom>
        Please sign in to access your dashboard and start exploring our features. 
        Don't forget to check out our tutorials and guides to get started, 
        contact support if you need any help!
      </MDTypography>
      <MDButton
        variant="contained"
        color="warning"
        onClick={() => navigate('/authentication/sign-in')}
        sx={{ mt: 3 }}
      >
        Sign In
      </MDButton>
    </MDBox>
  );
}

export default SubscriptionSuccess;